/**
 * Right-hand drawer for one trending TikTok song — same look as Discover's TrendDetailDrawer: a dark hero with the
 * TikTok embed of one of the song's top videos (prev/next + a numbered strip, one iframe mounted at a time), then the
 * song itself (preview in the shared bottom player), its rank/views over 30 days (views only when TikTok sends them —
 * many tracks only carry a daily rank), the clip IDs to attach when publishing with TikTok business tools, and
 * "Remix" (the page opens the Remix dialog).
 */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Copy, ExternalLink, Info, Loader2, ShieldCheck, Wand2 } from 'lucide-react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import type { AudioTrend, TrendSongClip } from '@/api/audioStudioApi';
import { LazyTikTokEmbed } from '@/components/tiktok/LazyTikTokEmbed';
import { tiktokVideoEmbedUrl } from '@/components/tiktok/embedUrl';
import { PlayButton, usePlayer } from './AudioPlayer';
import { JobProgress } from './JobProgress';
import { compactNumber, formatClock } from './format';
import { GenreTile } from './CoverArt';
import { RIGHTS } from './rights';
import { GenreChips, RankChange, RightsChip, TrendStatusBadge } from './TrendBits';
import { useTrendVideos, trendHasVideoSource } from './queries';
import { formatGrowth, hasTrendHistory, historySeries, rankDomain, trendGenres, trendPlayable, trendViews7d } from './trendInsights';

export interface TrendSongDrawerProps {
  trend: AudioTrend | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Remix" — the page closes the drawer and opens the Remix dialog. */
  onLookalike: (trend: AudioTrend) => void;
  lookalikePending?: boolean;
  jobId?: string;
  /** Region of the trends list, used to load the song's top videos. */
  region?: string;
  /** Show a generated genre tile instead of `cover_url` (missing or TikTok's shared default artwork). */
  placeholderCover?: boolean;
}

export function TrendSongDrawer({ trend, open, onOpenChange, onLookalike, lookalikePending = false, jobId, region = 'US', placeholderCover = false }: TrendSongDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* The close button sits on the dark hero — make it white. */}
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-[640px] [&>button:last-child]:text-white [&>button:last-child]:opacity-90">
        {/* Keyed so the selected video resets when another song is opened. */}
        {trend && <DrawerBody key={trend.trend_id} t={trend} onLookalike={() => onLookalike(trend)} pending={lookalikePending} jobId={jobId} region={region} cover={placeholderCover ? null : trend.cover_url} />}
      </SheetContent>
    </Sheet>
  );
}

function DrawerBody({ t, onLookalike, pending, jobId, region, cover }: { t: AudioTrend; onLookalike: () => void; pending: boolean; jobId?: string; region: string; cover: string | null }) {
  const series = useMemo(() => historySeries(t.history), [t.history]);
  const genres = trendGenres(t);
  const growth = formatGrowth(t.growth_pct);
  const views7d = trendViews7d(t);
  const hasViewSeries = series.some((p) => p.views != null);
  const ranks = rankDomain(series);
  const rankPoints = series.filter((p) => p.rank != null).length;
  return (
    <>
      <VideoHero t={t} region={region} cover={cover} genre={genres[0] ?? null} />
      <div className="flex flex-col gap-5 p-5">
        <SheetHeader className="p-0 text-left">
          <div className="flex items-start gap-3">
            <PlayButton item={trendPlayable(t)} coverUrl={cover} fallback={<GenreTile genre={genres[0] ?? null} title={t.title} size="thumb" />} />
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-[20px] leading-tight">{t.title}</SheetTitle>
              <SheetDescription className="text-[13px]">
                {t.artist || 'Unknown artist'}{t.duration_s ? ` · ${formatClock(t.duration_s)}` : ''}
              </SheetDescription>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <TrendStatusBadge status={t.trend_status} />
                <RightsChip kind="tiktok" />
                <GenreChips genres={genres} max={6} />
              </div>
            </div>
          </div>
          {!t.preview_url && <p className="text-[11.5px] text-[#98a2b3]">Preview refreshing — TikTok preview links expire after a few hours; try again shortly.</p>}
        </SheetHeader>

        {/* TikTok sends only a daily rank for many tracks: missing views / growth are hidden, not shown as "—". */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Rank now">
            <span className="flex items-baseline gap-1.5">#{t.rank} <RankChange v={t.rank_change} /></span>
          </StatTile>
          {t.views_latest != null && <StatTile label="Views · latest day">{compactNumber(t.views_latest)}</StatTile>}
          {views7d != null && <StatTile label="Views · 7 days">{compactNumber(views7d)}</StatTile>}
          {growth && (
            <StatTile label="Growth">
              <span className={cn((t.growth_pct ?? 0) >= 0 ? 'text-[#027a48]' : 'text-[#b42318]')}>{growth}</span>
            </StatTile>
          )}
        </div>

        {hasTrendHistory(series) && (
          <section className="rounded-[12px] border border-[#eaecf0] bg-white p-4" aria-label="30-day trend">
            <h4 className="text-[13px] font-semibold text-[#101828]">30-day trend</h4>
            <p className="text-[11px] text-gray-500">
              {hasViewSeries ? 'Daily views on TikTok (bars) and chart rank (line — higher is better).' : 'Daily chart rank on TikTok (higher is better).'}
            </p>
            <div className="mt-3 h-[220px] w-full">
              <ResponsiveContainer>
                <ComposedChart data={series} margin={{ top: 6, right: 4, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={16} />
                  {hasViewSeries && (
                    <YAxis
                      yAxisId="views"
                      tick={{ fontSize: 10 }}
                      width={44}
                      tickFormatter={(v: number) => compactNumber(v)}
                      label={{ value: 'Daily views', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#667085', dy: 30 }}
                    />
                  )}
                  <YAxis
                    yAxisId="rank"
                    type="number"
                    orientation="right"
                    reversed
                    allowDecimals={false}
                    domain={ranks ?? [1, 'auto']}
                    tick={{ fontSize: 10 }}
                    width={36}
                    tickFormatter={(v: number) => `#${v}`}
                    label={{ value: 'Rank', angle: 90, position: 'insideRight', fontSize: 10, fill: '#667085' }}
                  />
                  <Tooltip
                    formatter={(v: number, name: string) => (name === 'Rank' ? `#${v}` : compactNumber(v))}
                    labelStyle={{ fontSize: 11 }}
                    contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {hasViewSeries && <Bar yAxisId="views" dataKey="views" name="Daily views" fill="#98a2b3" fillOpacity={0.45} radius={[2, 2, 0, 0]} isAnimationActive={false} />}
                  {/*
                    No draw-in animation: inside the sliding Sheet the ResponsiveContainer first measures 0 px, and the
                    animated stroke-dasharray then leaves the line invisible (axes but no line). Dots when the history
                    is short so a sparse, rank-only series still reads as a line.
                  */}
                  <Line
                    yAxisId="rank"
                    dataKey="rank"
                    name="Rank"
                    type="monotone"
                    stroke="#6941c6"
                    strokeWidth={2}
                    dot={rankPoints <= 12 ? { r: 2.5, strokeWidth: 0, fill: '#6941c6' } : false}
                    activeDot={{ r: 4 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        <section className="rounded-[12px] border border-[#eaecf0] bg-white p-4" aria-label="Clip IDs">
          <h4 className="text-[13px] font-semibold text-[#101828]">Use this sound when publishing</h4>
          <p className="text-[11px] text-gray-500">Paste a clip ID into TikTok’s business publishing tools to attach this song to a post.</p>
          <div className="mt-3 flex flex-col gap-2">
            <ClipRow label="Trending clip ID" clip={t.clips?.trending ?? null} />
            <ClipRow label="Full song clip ID" clip={t.clips?.full ?? null} />
            {t.commercial_music_id && <IdRow label="Commercial music ID" value={t.commercial_music_id} />}
          </div>
          <p className="mt-3 flex items-start gap-1.5 text-[11.5px] text-[#475467]">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#98a2b3]" aria-hidden />
            {RIGHTS.tiktok.tooltip}
          </p>
        </section>

        <section className="rounded-[12px] border border-[#eaecf0] bg-[#f9fafb] p-4" aria-label="Lookalike">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-[13px] font-semibold text-[#101828]">Make it yours</h4>
              <p className="mt-0.5 flex items-start gap-1.5 text-[11.5px] text-[#667085]">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                Opens Remix — add any instructions, pick a length, then Generate. Remixes are original compositions in the song’s style, never a copy of its melody or lyrics.
              </p>
            </div>
            <Button size="sm" onClick={onLookalike} disabled={pending} title="Make an original track in this song’s style — tempo, instruments, energy" className="h-9 gap-1.5 rounded-[12px] bg-[#be43ff] text-[12.5px] text-white hover:bg-[#a835e0]">
              {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Wand2 className="h-3.5 w-3.5" aria-hidden />}
              Remix
            </Button>
          </div>
          {jobId && <div className="mt-3"><JobProgress jobId={jobId} compact /></div>}
        </section>
      </div>
    </>
  );
}

/** The dark 9:16 hero: the selected top video (default first) with prev/next and a numbered strip. */
function VideoHero({ t, region, cover, genre }: { t: AudioTrend; region: string; cover: string | null; genre: string | null }) {
  const inline = t.top_videos ?? [];
  // Real TikTok songs (cml-…) get their videos from a per-song call (cached with the trend card's).
  const loaded = useTrendVideos(t.trend_id, region, inline.length === 0);
  const videos = inline.length ? inline : loaded.data ?? [];
  const [index, setIndex] = useState(0);
  const { isPlaying, pauseResume } = usePlayer();
  const video = videos[index];
  const openUrl = video?.share_url || (index === 0 ? t.tiktok_url : null);

  // Switching video pauses the song preview so the two don't play over each other.
  const select = (i: number) => {
    if (i === index || i < 0 || i >= videos.length) return;
    if (isPlaying) pauseResume();
    setIndex(i);
  };

  if (!video) {
    return (
      <div className="flex w-full flex-col items-center gap-3 bg-black px-4 pb-6 pt-8 text-center">
        {cover
          ? <img src={cover} alt="" className="h-28 w-28 rounded-xl object-cover" />
          : <span className="h-28 w-28 overflow-hidden rounded-xl"><GenreTile genre={genre} title={t.title} /></span>}
        <p className="max-w-xs text-[12.5px] text-white/75">
          {loaded.isLoading ? 'Loading top TikTok videos…'
            : trendHasVideoSource(t.trend_id) ? 'No TikTok videos found for this song yet.'
            : 'Top videos appear once TikTok is connected.'}
        </p>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-black px-4 pb-4 pt-5">
      <div className="flex items-center justify-center gap-2">
        <HeroArrow dir="prev" disabled={index === 0} onClick={() => select(index - 1)} />
        <div className="aspect-[9/16] max-h-[520px] w-full max-w-[292px] overflow-hidden rounded-xl bg-black">
          {/* One iframe at a time: the key remounts it for the newly selected video. */}
          <LazyTikTokEmbed
            key={video.video_id}
            src={tiktokVideoEmbedUrl(video)}
            title={`TikTok video ${index + 1} using ${t.title}`}
            lazy={false}
            className="h-full w-full"
            iframeClassName="h-full w-full border-0"
          />
        </div>
        <HeroArrow dir="next" disabled={index >= videos.length - 1} onClick={() => select(index + 1)} />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <span className="rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-900">
          Video {index + 1} of {videos.length}
        </span>
        {openUrl && (
          <a
            href={openUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-900 hover:bg-white"
          >
            <ExternalLink className="h-3 w-3" aria-hidden /> Open on TikTok
          </a>
        )}
      </div>
      {videos.length > 1 && (
        <div role="tablist" aria-label="Top videos" className="mx-auto mt-3 flex max-w-full gap-1.5 overflow-x-auto pb-1 [scrollbar-width:thin] sm:justify-center">
          {videos.map((v, i) => (
            <button
              key={v.video_id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Video ${i + 1}`}
              onClick={() => select(i)}
              className={cn(
                'flex h-9 w-7 shrink-0 items-center justify-center rounded-md text-[11px] tabular-nums transition-colors',
                i === index ? 'bg-white text-[#101828]' : 'bg-white/10 text-white/70 hover:bg-white/20',
              )}
              style={{ fontWeight: 600 }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HeroArrow({ dir, disabled, onClick }: { dir: 'prev' | 'next'; disabled: boolean; onClick: () => void }) {
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === 'prev' ? 'Previous video' : 'Next video'}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30"
    >
      <Icon className="h-5 w-5" aria-hidden />
    </button>
  );
}

function StatTile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[12px] border border-[#eaecf0] bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-[18px] font-semibold tabular-nums text-[#101828]">{children}</div>
    </div>
  );
}

async function copyId(value: string, what: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${what} copied`);
  } catch {
    toast.error('Could not copy — select the ID and copy it manually');
  }
}

function IdRow({ label, value, meta }: { label: string; value: string; meta?: string | null }) {
  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-[#f2f4f7] bg-[#f9fafb] px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-gray-500">{label}{meta ? ` · ${meta}` : ''}</div>
        <code className="block truncate font-mono text-[12.5px] text-[#101828] select-all">{value}</code>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={() => void copyId(value, label)} aria-label={`Copy ${label}`} className="h-8 shrink-0 gap-1.5 text-[12px]">
        <Copy className="h-3.5 w-3.5" aria-hidden /> Copy
      </Button>
    </div>
  );
}

function ClipRow({ label, clip }: { label: string; clip: TrendSongClip | null }) {
  if (!clip) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 px-3 py-2 text-[11.5px] text-gray-400">{label} — not available yet</div>
    );
  }
  return <IdRow label={label} value={clip.song_clip_id} meta={clip.duration_s ? formatClock(clip.duration_s) : null} />;
}
